import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axiosInstance, { authAxios, setTenantDomain } from '@/utils/axios'
import router from '@/router'

export const useAuthStore = defineStore('auth', () => {
  const user = ref(null)
  const access = ref(localStorage.getItem('access') || null)
  const refresh = ref(localStorage.getItem('refresh') || null)
  const domain = ref(localStorage.getItem('domain') || null)
  const role = ref(null)
  const error = ref(null)

  const isAuthenticated = computed(() => !!access.value)
  const isAdmin = computed(() => hasPermissions('has_admin_status'))
  const isJuniorRep = computed(() => role.value?.name === 'juniorrep')

  try {
    const storedRole = localStorage.getItem('role')
    role.value = storedRole ? JSON.parse(storedRole) : null
  } catch (err) {
    console.error('Failed to parse role from localStorage', err)
    role.value = null
  }

  if (access.value) {
    axiosInstance.defaults.headers.common['Authorization'] = `JWT ${access.value}`
    authAxios.defaults.headers.common['Authorization'] = `JWT ${access.value}`
  }

  if (domain.value) {
    setTenantDomain(domain.value)
  }

  function hasPermissions(...permissions) {
    if (!role.value || !Array.isArray(role.value.permissions)) return false
    return permissions.every((perm) =>
      role.value.permissions.some((permissionObj) => permissionObj.name === perm)
    )
  }

  async function login(email, password, redirect = '/leads') {
    error.value = null
    try {
      const response = await authAxios.post('/auth/jwt/create/', { email, password })
      saveTokens(response.data.access, response.data.refresh)
      await fetchUserData()
      router.replace(redirect)
    } catch (err) {
      if (err.response && err.response.status === 401) {
        error.value = 'Incorrect email or password'
      } else {
        error.value = 'Failed to login. Please try again later.'
      }
    }
  }

  async function fetchUserData() {
    try {
      const response = await authAxios.get('/auth/users/me/')
      user.value = response.data
      role.value = user.value.role
      localStorage.setItem('role', JSON.stringify(role.value))
      if (user.value.tenant && user.value.tenant.domain) {
        setTenantDomain(user.value.tenant.domain)
        domain.value = user.value.tenant.domain
      }
    } catch (err) {
      await logout()
    }
  }

  async function logout() {
    console.info('Logging out')
    clearTokens()
    user.value = null
    domain.value = null
    role.value = null
    localStorage.removeItem('role')
    router.push('/')
  }

  async function loadTokens() {
    if (access.value && (await isTokenValid(access.value))) {
      await fetchUserData()
    } else if (refresh.value && (await isTokenValid(refresh.value))) {
      try {
        await refreshToken()
      } catch {
        await logout()
      }
    } else {
      await logout()
    }
  }

  async function refreshToken() {
    try {
      const response = await authAxios.post('/auth/jwt/refresh/', { refresh: refresh.value })
      saveTokens(response.data.access, refresh.value)
      await fetchUserData()
    } catch (err) {
      await logout()
      throw err
    }
  }

  async function isTokenValid(token) {
    try {
      await authAxios.post('/auth/jwt/verify/', { token })
      return true
    } catch {
      return false
    }
  }

  function saveTokens(accessToken, refreshToken) {
    access.value = accessToken
    refresh.value = refreshToken
    axiosInstance.defaults.headers.common['Authorization'] = `JWT ${accessToken}`
    authAxios.defaults.headers.common['Authorization'] = `JWT ${accessToken}`
    localStorage.setItem('access', accessToken)
    localStorage.setItem('refresh', refreshToken)
  }

  function clearTokens() {
    access.value = null
    refresh.value = null
    delete axiosInstance.defaults.headers.common['Authorization']
    delete authAxios.defaults.headers.common['Authorization']
    localStorage.removeItem('access')
    localStorage.removeItem('refresh')
  }

  return {
    user,
    access,
    refresh,
    error,
    domain,
    role,
    isAuthenticated,
    isAdmin,
    isJuniorRep,
    hasPermissions,
    login,
    logout,
    loadTokens,
    refreshToken
  }
})
